import * as React from "react";
import { useSelector } from "react-redux";
import { useState } from "react";
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from "@mui/material";
import { DateTime } from "luxon";
import { DISPLAY_DATE_FORMAT } from "./DataTable";

/**
 * Recalculation approval - some edits trigger an index recalculation, which the user has to approve before saving.
 * The backend returns `recalculation` (without saving) - { start_date, index_id (null = all indexes), delay_minutes } -
 * and the request is then resent with `recalculation_approval` = "scheduled" (runs after the delay) / "immediate"
 * (due right away). Only the backend decides if it's needed - any response without `recalculation` was saved.
 *
 * There's no API for pending recalculations, so we can confirm the approval but not track the run.
 */

const formatMinutes = (minutes) =>
    `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;

/**
 * Hook that handles the recalculation approval of an API call (see above).
 *
 * Returns:
 *  - withRecalculationApproval: receives `submit` - an async function that calls the API with the given extra body
 *      params (merged into the request body) and returns the response. Returns a function with the same signature
 *      that also asks the user to approve a recalculation, if the response requires one, and calls `submit` again with
 *      `recalculation_approval`. It returns the final response, or null if the user cancelled.
 *      The approval is kept for further calls of the returned function, so the user isn't asked again when the request
 *      is resent for another confirmation (see `useFutureActionsConfirmation`) - so use a new one for each submission.
 *  - recalculationDialog: the dialog element - must be rendered by the caller
 */
export function useRecalculationApproval() {
    const indexesById = useSelector((state) => state.indexes.indexesById);
    // Currently displayed dialog - { mode: "confirm" / "summary", recalculation, approval, resolve }
    const [pending, setPending] = useState(null);

    const showDialog = (mode, recalculation, approval) =>
        new Promise((resolve) =>
            setPending({ mode, recalculation, approval, resolve }),
        );

    const closeDialog = (result) => {
        pending.resolve(result);
        setPending(null);
    };

    const withRecalculationApproval = (submit) => {
        let approved = null;
        const approvalBody = () =>
            approved ? { recalculation_approval: approved.approval } : {};

        return async (extraBody) => {
            let response = await submit({ ...extraBody, ...approvalBody() });

            while (response.data?.recalculation) {
                // Nothing was saved - resolves to "scheduled" / "immediate" / null (cancel)
                const recalculation = response.data.recalculation;
                const approval = await showDialog("confirm", recalculation);
                if (approval === null) return null;

                approved = { recalculation, approval };
                response = await submit({ ...extraBody, ...approvalBody() });
            }

            // With future actions nothing was saved yet - the request will be resent after that confirmation
            if (approved && !response.data?.future_actions?.length) {
                await showDialog(
                    "summary",
                    approved.recalculation,
                    approved.approval,
                );
            }
            return response;
        };
    };

    const isSummary = pending?.mode === "summary";

    const renderConfirmation = ({ start_date, index_id, delay_minutes }) => {
        const index = indexesById[index_id];
        const indexName = index
            ? `${index.internal_id} - ${index.name}`
            : index_id;
        const startDate =
            DateTime.fromMillis(start_date).toFormat(DISPLAY_DATE_FORMAT);

        return (
            <>
                <DialogContent>
                    <Alert severity="warning">
                        Nothing was saved yet - this edit requires recalculating{" "}
                        {index_id ? indexName : "all indexes"} from {startDate}.
                    </Alert>
                </DialogContent>
                <DialogActions
                    disableSpacing
                    sx={{
                        flexWrap: "wrap",
                        gap: 2
                    }}
                >
                    <Button
                        variant="contained"
                        onClick={() => closeDialog("scheduled")}
                    >
                        Save and Recalculate in {formatMinutes(delay_minutes)}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => closeDialog("immediate")}
                    >
                        Save and Recalculate Now
                    </Button>
                    <Button onClick={() => closeDialog(null)}>Cancel</Button>
                </DialogActions>
            </>
        );
    };

    const renderSummary = ({ delay_minutes }, approval) => (
        <>
            <DialogContent>
                <Alert severity="success">
                    The edit was saved.{" "}
                    {approval === "scheduled"
                        ? `A recalculation will start in ${formatMinutes(
                            delay_minutes,
                        )}.`
                        : "The recalculation is due right away."}
                </Alert>
            </DialogContent>
            <DialogActions>
                <Button variant="contained" onClick={() => closeDialog(null)}>
                    OK
                </Button>
            </DialogActions>
        </>
    );

    const recalculationDialog = (
        <Dialog
            open={pending !== null}
            // Closing the confirmation = cancel (the summary's result is ignored)
            onClose={() => closeDialog(null)}
            maxWidth="sm"
            fullWidth={true}
        >
            {pending && (
                <>
                    <DialogTitle>
                        {isSummary
                            ? "Recalculation Approved"
                            : "Recalculation Needed"}
                    </DialogTitle>
                    {isSummary
                        ? renderSummary(pending.recalculation, pending.approval)
                        : renderConfirmation(pending.recalculation)}
                </>
            )}
        </Dialog>
    );

    return { withRecalculationApproval, recalculationDialog };
}
